import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { invoiceService, clientService, engagementService, timeEntryService } from '../services/api';

export default function InvoiceEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reference data
  const [clients, setClients] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [unbilledTimeEntries, setUnbilledTimeEntries] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    clientId: searchParams.get('client') || '',
    engagementId: searchParams.get('engagement') || '',
    amount: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    notes: '',
    internalNotes: '',
  });
  const [selectedTimeEntries, setSelectedTimeEntries] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, [id]);

  useEffect(() => {
    // When client changes, filter engagements
    if (formData.clientId) {
      loadEngagementsForClient(formData.clientId);
    } else {
      setEngagements([]);
      setFormData((prev) => ({ ...prev, engagementId: '' }));
    }
  }, [formData.clientId]);

  useEffect(() => {
    // When engagement changes, load unbilled time entries
    if (formData.engagementId) {
      loadUnbilledTimeEntries(formData.engagementId);
    } else {
      setUnbilledTimeEntries([]);
      setSelectedTimeEntries([]);
    }
  }, [formData.engagementId]);

  useEffect(() => {
    // Auto-calculate amount from selected time entries
    if (selectedTimeEntries.length > 0) {
      const total = unbilledTimeEntries
        .filter((te) => selectedTimeEntries.includes(te.id))
        .reduce((sum, te) => sum + (te.hours * (te.rate || 0)), 0);
      setFormData((prev) => ({ ...prev, amount: total.toFixed(2) }));
    }
  }, [selectedTimeEntries, unbilledTimeEntries]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load clients
      const clientsData = await clientService.getAll();
      setClients(clientsData.filter((c) => c.status === 'active'));

      // If editing, load the invoice
      if (isEditing) {
        const invoice = await invoiceService.getById(id);
        setFormData({
          clientId: invoice.client?.id?.toString() || '',
          engagementId: invoice.engagement?.id?.toString() || '',
          amount: invoice.amount?.toString() || '',
          issueDate: invoice.issueDate ? new Date(invoice.issueDate).toISOString().split('T')[0] : '',
          dueDate: invoice.dueDate ? new Date(invoice.dueDate).toISOString().split('T')[0] : '',
          notes: invoice.notes || '',
          internalNotes: invoice.internalNotes || '',
        });
        // Time entries already on this invoice are pre-selected
        if (invoice.timeEntries) {
          setSelectedTimeEntries(invoice.timeEntries.map((te) => te.id));
        }
      } else if (searchParams.get('client')) {
        // Pre-load engagements if client is provided via URL
        await loadEngagementsForClient(searchParams.get('client'));
      }

      // Set default due date to 30 days from issue date
      if (!isEditing && !formData.dueDate) {
        const defaultDue = new Date();
        defaultDue.setDate(defaultDue.getDate() + 30);
        setFormData((prev) => ({ ...prev, dueDate: defaultDue.toISOString().split('T')[0] }));
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadEngagementsForClient = async (clientId) => {
    try {
      const allEngagements = await engagementService.getAll();
      const filtered = allEngagements.filter(
        (e) => e.client?.id?.toString() === clientId.toString()
      );
      setEngagements(filtered);

      // If we have an engagement from URL params, load its time entries
      const engagementParam = searchParams.get('engagement');
      if (engagementParam && filtered.some((e) => e.id.toString() === engagementParam)) {
        setFormData((prev) => ({ ...prev, engagementId: engagementParam }));
      }
    } catch (err) {
      console.error('Error loading engagements:', err);
    }
  };

  const loadUnbilledTimeEntries = async (engagementId) => {
    try {
      const entries = await timeEntryService.getUnbilled(engagementId);
      setUnbilledTimeEntries(entries);
      // Auto-select all unbilled entries
      setSelectedTimeEntries(entries.map((e) => e.id));
    } catch (err) {
      console.error('Error loading time entries:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTimeEntryToggle = (entryId) => {
    setSelectedTimeEntries((prev) =>
      prev.includes(entryId)
        ? prev.filter((id) => id !== entryId)
        : [...prev, entryId]
    );
  };

  const handleSelectAllTimeEntries = () => {
    if (selectedTimeEntries.length === unbilledTimeEntries.length) {
      setSelectedTimeEntries([]);
    } else {
      setSelectedTimeEntries(unbilledTimeEntries.map((e) => e.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        clientId: parseInt(formData.clientId),
        engagementId: formData.engagementId ? parseInt(formData.engagementId) : null,
        amount: parseFloat(formData.amount) || 0,
        issueDate: formData.issueDate || null,
        dueDate: formData.dueDate || null,
        notes: formData.notes,
        internalNotes: formData.internalNotes,
        timeEntryIds: selectedTimeEntries,
      };

      let result;
      if (isEditing) {
        result = await invoiceService.update(id, payload);
      } else {
        result = await invoiceService.create(payload);
      }

      navigate(`/invoices/${result.id}`);
    } catch (err) {
      console.error('Error saving invoice:', err);
      setError(err.response?.data?.message || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return '$' + Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEditing ? 'Edit Invoice' : 'New Invoice'} | CR Hood Admin</title>
      </Helmet>

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-6">
          <Link to="/invoices" className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Invoice' : 'Create Invoice'}
          </h1>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client & Engagement Selection */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Invoice For</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Client *</label>
                <select
                  name="clientId"
                  value={formData.clientId}
                  onChange={handleChange}
                  className="form-input"
                  required
                >
                  <option value="">Select a client...</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.company || client.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Engagement (Optional)</label>
                <select
                  name="engagementId"
                  value={formData.engagementId}
                  onChange={handleChange}
                  className="form-input"
                  disabled={!formData.clientId}
                >
                  <option value="">No specific engagement</option>
                  {engagements.map((engagement) => (
                    <option key={engagement.id} value={engagement.id}>
                      {engagement.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Time Entries Selection */}
          {formData.engagementId && unbilledTimeEntries.length > 0 && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">
                  Unbilled Time Entries ({unbilledTimeEntries.length})
                </h2>
                <button
                  type="button"
                  onClick={handleSelectAllTimeEntries}
                  className="text-sm text-accent hover:underline"
                >
                  {selectedTimeEntries.length === unbilledTimeEntries.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {unbilledTimeEntries.map((entry) => (
                  <label
                    key={entry.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedTimeEntries.includes(entry.id)
                        ? 'bg-accent/5 border-accent'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={selectedTimeEntries.includes(entry.id)}
                        onChange={() => handleTimeEntryToggle(entry.id)}
                        className="rounded border-gray-300 text-accent focus:ring-accent"
                      />
                      <div>
                        <div className="font-medium text-sm">{entry.description}</div>
                        <div className="text-xs text-gray-500">
                          {formatDate(entry.date)} • {entry.hours} hrs @ {formatCurrency(entry.rate)}/hr
                        </div>
                      </div>
                    </div>
                    <div className="font-medium text-sm">
                      {formatCurrency(entry.hours * (entry.rate || 0))}
                    </div>
                  </label>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  {selectedTimeEntries.length} of {unbilledTimeEntries.length} entries selected
                </span>
                <span className="font-semibold text-accent">
                  Total: {formatCurrency(
                    unbilledTimeEntries
                      .filter((te) => selectedTimeEntries.includes(te.id))
                      .reduce((sum, te) => sum + (te.hours * (te.rate || 0)), 0)
                  )}
                </span>
              </div>
            </div>
          )}

          {formData.engagementId && unbilledTimeEntries.length === 0 && (
            <div className="card bg-yellow-50 border-yellow-200">
              <p className="text-yellow-700 text-sm">
                No unbilled time entries found for this engagement. You can still create a manual invoice below.
              </p>
            </div>
          )}

          {/* Invoice Details */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Invoice Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Amount *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    className="form-input pl-7"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    required
                  />
                </div>
                {selectedTimeEntries.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">Auto-calculated from selected time entries</p>
                )}
              </div>
              <div>
                <label className="form-label">Issue Date</label>
                <input
                  type="date"
                  name="issueDate"
                  value={formData.issueDate}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Notes</h2>
            <div className="space-y-4">
              <div>
                <label className="form-label">Invoice Notes (visible to client)</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="form-input"
                  rows={3}
                  placeholder="Payment terms, special instructions, etc."
                />
              </div>
              <div>
                <label className="form-label">Internal Notes (not visible to client)</label>
                <textarea
                  name="internalNotes"
                  value={formData.internalNotes}
                  onChange={handleChange}
                  className="form-input"
                  rows={2}
                  placeholder="Internal notes about this invoice..."
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <Link to="/invoices" className="btn btn-secondary">
              Cancel
            </Link>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : isEditing ? 'Update Invoice' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
